import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationsResponseSchema } from '@unit/shared';
import { apiGet } from '../../lib/api.js';

/** Dispatched on `window` after notifications were marked read, so the badge refreshes at once. */
export const NOTIFICATIONS_CHANGED = 'unit:notifications-changed';

/**
 * The unread count in the header (REQUIREMENTS §2.4 notifications, in-app
 * only). Polled on navigation and every minute — a notification is written
 * inside the transaction that caused it, and the person it is for is
 * usually not looking at the screen when it lands.
 */
export function NotificationsBell() {
  const { t } = useTranslation();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      apiGet('/notifications', notificationsResponseSchema)
        .then((r) => {
          if (!cancelled) setUnread(r.unread);
        })
        .catch(() => {
          /* the badge is a convenience; the page itself reports errors */
        });
    void load();
    const handle = setInterval(() => void load(), 60_000);
    // The notifications page marks things read; it says so on the window.
    const onChanged = () => void load();
    window.addEventListener(NOTIFICATIONS_CHANGED, onChanged);
    return () => {
      cancelled = true;
      clearInterval(handle);
      window.removeEventListener(NOTIFICATIONS_CHANGED, onChanged);
    };
  }, [location.pathname]);

  return (
    <Link
      to="/notifications"
      aria-label={t('notifications.bell', { count: unread })}
      className="relative rounded-lg px-2 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100"
      data-testid="notifications-bell"
      data-unread={unread}
    >
      <span aria-hidden="true">🔔</span>
      {unread > 0 && (
        <span className="latin-inline absolute -top-1.5 -end-1.5 min-w-5 rounded-full bg-red-600 px-1 text-center text-xs font-semibold text-white" data-testid="notifications-unread">
          {unread}
        </span>
      )}
    </Link>
  );
}
